(() => {
  if (customElements.get('superbia-cart-recommendations')) return;
  let retained = null;
  const membership = ids => [...new Set(ids.map(String))].filter(id => /^\d+$/.test(id)).sort().join(',');

  document.addEventListener('cart-update', event => {
    if (retained && Array.isArray(event.detail?.items) &&
        membership(event.detail.items.map(item => item.product_id)) !== retained.membership) {
      retained.controller.abort();
      retained = null;
    }
  });

  class CartRecommendations extends HTMLElement {
    connectedCallback() {
      this.hidden = true;
      this.loading = false;
      this.loaded = false;
      this.controller = new AbortController();
      this.track = this.querySelector('.superbia-cart-recommendations-track');
      this.previous = this.querySelector('[data-recommendations-prev]');
      this.next = this.querySelector('[data-recommendations-next]');
      if (!this.track || !this.previous || !this.next) return;
      this.previous.addEventListener('click', () => this.move(-1), {signal: this.controller.signal});
      this.next.addEventListener('click', () => this.move(1), {signal: this.controller.signal});
      this.track.addEventListener('scroll', () => this.updateControls(), {passive: true, signal: this.controller.signal});
      this.resizeObserver = new ResizeObserver(() => this.updateControls());
      this.resizeObserver.observe(this.track);
      this.observer = new MutationObserver(() => this.loadIfOpen());
      this.observer.observe(document.body, {attributes: true, attributeFilter: ['class']});
      this.loadIfOpen();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.observer?.disconnect();
      this.resizeObserver?.disconnect();
    }

    move(direction) {
      const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollBy({left: direction * rtl * this.track.clientWidth,
        behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth'});
    }

    updateControls() {
      const width = this.track.clientWidth;
      const position = Math.abs(this.track.scrollLeft);
      if (width > 0 && this.loaded && retained === this.state) this.state.index = Math.round(position / width);
      this.previous.disabled = position < 2;
      this.next.disabled = position >= this.track.scrollWidth - width - 2;
    }

    async fetchCards(id, controller) {
      const root = window.Shopify?.routes?.root || '/';
      const url = new URL(`${root}recommendations/products`, location.origin);
      url.search = new URLSearchParams({product_id: id, intent: 'related', limit: '10', section_id: 'cart-auto-recommendations'});
      const timeout = new AbortController();
      const abort = () => timeout.abort();
      controller.signal.addEventListener('abort', abort, {once: true});
      const timer = setTimeout(abort, 8000);
      try {
        const response = await fetch(url, {signal: timeout.signal, credentials: 'same-origin'});
        if (!response.ok) return [];
        const html = new DOMParser().parseFromString(await response.text(), 'text/html');
        return [...html.querySelectorAll('[data-recommendation-id][data-recommendation-available="true"]')];
      } catch (_) {
        return [];
      } finally {
        clearTimeout(timer);
        controller.signal.removeEventListener('abort', abort);
      }
    }

    async loadIfOpen() {
      if (this.loaded || this.loading || !document.body.classList.contains('cart-sidebar-show')) return;
      let ids;
      try { ids = [...new Set(JSON.parse(this.dataset.productIds).map(String))].filter(id => /^\d+$/.test(id)); }
      catch (_) { return; }
      if (!ids.length) return;
      const controller = this.controller;
      this.loading = true;
      const limit = Math.min(5, Math.max(1, Number(this.dataset.limit) || 3));
      const signature = membership(ids);
      const key = JSON.stringify([signature, limit, window.Shopify?.routes?.root || '/', document.documentElement.lang]);
      if (!retained || retained.key !== key) {
        retained?.controller.abort();
        retained = {key, membership: signature, controller: new AbortController(), cards: null, index: 0};
        retained.promise = this.loadCards(ids, limit, retained);
      }
      const state = retained;
      this.state = state;
      if (state.cards === null) await state.promise;
      if (!this.isConnected || controller.signal.aborted || this.controller !== controller || retained !== state) return;
      this.track.replaceChildren(...state.cards);
      this.track.tabIndex = state.cards.length > 1 ? 0 : -1;
      this.hidden = state.cards.length === 0;
      this.loaded = true;
      this.loading = false;
      const rtl = getComputedStyle(this.track).direction === 'rtl' ? -1 : 1;
      this.track.scrollLeft = state.index * this.track.clientWidth * rtl;
      this.updateControls();
    }

    async loadCards(ids, limit, state) {
      const groups = new Array(ids.length);
      let cursor = 0;
      // Bound simultaneous requests while representing every distinct cart product.
      const worker = async () => {
        while (cursor < ids.length && !state.controller.signal.aborted) {
          const index = cursor++;
          groups[index] = await this.fetchCards(ids[index], state.controller);
        }
      };
      await Promise.all(Array.from({length: Math.min(3, ids.length)}, worker));
      if (state.controller.signal.aborted) return;
      const seen = new Set(ids);
      const selected = [];
      // Interleave Shopify's per-product rankings instead of letting one seed dominate.
      for (let rank = 0; rank < 10 && selected.length < limit; rank++) {
        for (const cards of groups) {
          const card = cards?.[rank];
          if (!card) continue;
          const id = card.dataset.recommendationId;
          if (!/^\d+$/.test(id) || seen.has(id)) continue;
          seen.add(id);
          selected.push(card);
          if (selected.length === limit) break;
        }
      }
      // Keep the actual card/image nodes across Ella's quantity-only drawer replacement.
      state.cards = selected;
    }
  }

  customElements.define('superbia-cart-recommendations', CartRecommendations);
})();
